import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastData, ToastSnackbarComponent, ToastType } from './toast/toast-snackbar.component';

export interface ToastOptions {
  duration?: number;
  /** ngx-toastr compat alias for `duration` (ms) */
  timeOut?: number;
  enableHtml?: boolean;
  /** Label for an optional action button; subscribe via `ref.onAction()`. */
  actionLabel?: string;
  /** ngx-toastr compat no-ops — the new toast card always shows a close button + progress bar */
  extendedTimeOut?: number;
  closeButton?: boolean;
  progressBar?: boolean;
}

const DEFAULT_DURATION = 5000;

const DEFAULT_TITLES: Record<ToastType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private currentKey: string | null = null;
  private currentRef: ReturnType<MatSnackBar['openFromComponent']> | null = null;

  constructor(private snackBar: MatSnackBar) {}

  /** The toast currently on screen, or null when none is showing. */
  get activeRef() {
    return this.currentRef;
  }

  success(message: string, title?: string, options?: ToastOptions) {
    return this.show('success', message, title, options);
  }

  error(message: string, title?: string, options?: ToastOptions) {
    return this.show('error', message, title, options);
  }

  warning(message: string, title?: string, options?: ToastOptions) {
    return this.show('warning', message, title, options);
  }

  info(message: string, title?: string, options?: ToastOptions) {
    return this.show('info', message, title, options);
  }

  clear() {
    this.snackBar.dismiss();
  }

  private show(type: ToastType, message: string, title?: string, options?: ToastOptions) {
    const duration = options?.duration ?? options?.timeOut ?? DEFAULT_DURATION;
    const key = `${type}:${title ?? ''}:${message}`;

    // Several requests can fail at once (e.g. a batch of parallel calls all
    // getting a 401 on session expiry), each calling toastr.error with the
    // same message. Opening a fresh snack bar for every one of them replaces
    // the previous toast and restarts its progress bar, which looks like the
    // bar is repeatedly jumping/resetting. If the same toast is still open,
    // just leave it be instead of re-triggering it.
    if (this.currentKey === key && this.currentRef) {
      return this.currentRef;
    }

    const data: ToastData = {
      type,
      title: title || DEFAULT_TITLES[type],
      message,
      duration,
      enableHtml: options?.enableHtml,
      actionLabel: options?.actionLabel,
    };
    const ref = this.snackBar.openFromComponent(ToastSnackbarComponent, {
      data,
      // No `duration` here on purpose — the toast component manages its own
      // dismiss timer so it can pause it on hover instead of auto-closing.
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['toast-snackbar-panel'],
    });
    this.currentKey = key;
    this.currentRef = ref;
    ref.afterDismissed().subscribe(() => {
      if (this.currentRef === ref) {
        this.currentKey = null;
        this.currentRef = null;
      }
    });
    return ref;
  }
}
